/**
 * device/state.ts — The Thing In Your Pocket
 *
 * Every player carries two devices: an hPhone (portrait, one-handed)
 * and an hPad (landscape, two-handed). Each has a custom shell color
 * chosen at character creation. The device is a diegetic UI — it exists
 * in the game world as an object the character physically holds.
 *
 * This module defines the state shape. The overlay renderer and app
 * system consume it; the game loop mutates it.
 */

// ─── Device Case / Skin ───────────────────────────────────────────────────

/** A cosmetic case or skin that wraps the device shell */
export interface DeviceCaseStyle {
  id: string;
  name: string;
  /** Pattern type rendered on top of the shell color */
  pattern: "none" | "stripe" | "dots" | "gradient" | "carbon" | "marble" | "wood" | "custom";
  /** Secondary color for patterns that need one */
  patternColor: string;
  /** Opacity of the pattern overlay (0–1) */
  patternAlpha: number;
  /** Rarity for store/collection purposes */
  rarity: "common" | "uncommon" | "rare" | "legendary";
}

/** Built-in starter cases available to every player */
export const DEFAULT_CASES: DeviceCaseStyle[] = [
  {
    id: "clear",
    name: "Clear",
    pattern: "none",
    patternColor: "transparent",
    patternAlpha: 0,
    rarity: "common",
  },
  {
    id: "matte_black",
    name: "Matte Black",
    pattern: "none",
    patternColor: "#111111",
    patternAlpha: 0.72,
    rarity: "common",
  },
  {
    id: "carbon_fiber",
    name: "Carbon Fiber",
    pattern: "carbon",
    patternColor: "#1a1a1a",
    patternAlpha: 0.65,
    rarity: "uncommon",
  },
  {
    id: "marble_white",
    name: "Marble White",
    pattern: "marble",
    patternColor: "#c8c0b8",
    patternAlpha: 0.7,
    rarity: "uncommon",
  },
  {
    id: "rose_gold_stripe",
    name: "Rose Gold Stripe",
    pattern: "stripe",
    patternColor: "#e8a090",
    patternAlpha: 0.75,
    rarity: "uncommon",
  },
  {
    id: "ocean_gradient",
    name: "Ocean Gradient",
    pattern: "gradient",
    patternColor: "#2060d0",
    patternAlpha: 0.7,
    rarity: "rare",
  },
  {
    id: "woodland",
    name: "Woodland",
    pattern: "wood",
    patternColor: "#7a5230",
    patternAlpha: 0.68,
    rarity: "rare",
  },
  {
    id: "midnight_dots",
    name: "Midnight Dots",
    pattern: "dots",
    patternColor: "#4040a0",
    patternAlpha: 0.7,
    rarity: "uncommon",
  },
  {
    id: "aurora",
    name: "Aurora",
    pattern: "gradient",
    patternColor: "#40c090",
    patternAlpha: 0.65,
    rarity: "rare",
  },
  {
    id: "obsidian_weave",
    name: "Obsidian Weave",
    pattern: "carbon",
    patternColor: "#300830",
    patternAlpha: 0.78,
    rarity: "legendary",
  },
];

// ─── Device Configuration ─────────────────────────────────────────────────

/** Persistent config for a single device — survives sessions */
export interface DeviceConfig {
  /** Hex color for the device shell (#3a3a3a = space gray) */
  color: string;
  /** Model ID — "default" initially, expandable for shop skins */
  model: string;
  /** Equipped case skin — null means bare device */
  case?: DeviceCaseStyle;
}

// ─── Device Runtime State ─────────────────────────────────────────────────

/** Animation phase for the raise/lower transition */
export type DeviceAnimPhase = "raising" | "held" | "lowering" | "stowed";

/** Which physical device is active */
export type ActiveDeviceType = "phone" | "pad";

/** A pending notification badge on an app icon */
export interface DeviceNotification {
  appId: string;
  count: number;
}

/** Full runtime state for the device system */
export interface DeviceState {
  /** hPhone configuration (portrait, one-handed) */
  hPhone: DeviceConfig;
  /** hPad configuration (landscape, two-handed) */
  hPad: DeviceConfig;
  /** Which device is currently active, or null if stowed */
  activeDevice: ActiveDeviceType | null;
  /** Which app is open, or null for home screen */
  activeApp: string | null;
  /** Raise/lower animation phase */
  animPhase: DeviceAnimPhase;
  /** 0–1 progress through current animation phase */
  animProgress: number;
  /** Per-app persistent state — each app reads/writes its own slice */
  appStates: Record<string, unknown>;
  /** Notification badges for home screen */
  notifications: DeviceNotification[];
  /** Screen-space offset from center (user drags device around) */
  offsetX: number;
  offsetY: number;
  /** Current app content rect in screen coordinates (set by overlay each frame) */
  appContentRect: { x: number; y: number; w: number; h: number } | null;
  /** Pending text edit callback — set by apps to intercept ChatSubmit */
  editCallback: ((text: string) => void) | null;
  /** Placeholder text for the edit input */
  editPlaceholder: string;
  /** Pre-fill text for the edit input (consumed once when edit opens) */
  editPrefill: string;
}

// ─── Factory ──────────────────────────────────────────────────────────────

/** Create a fresh DeviceState with sensible defaults */
export function createDeviceState(): DeviceState {
  return {
    hPhone: { color: "#3a3a3a", model: "default" },  // space gray
    hPad:   { color: "#c0c0c0", model: "default" },  // silver
    activeDevice: null,
    activeApp: null,
    animPhase: "stowed",
    animProgress: 0,
    appStates: {},
    notifications: [],
    offsetX: 0,
    offsetY: 0,
    appContentRect: null,
    editCallback: null,
    editPlaceholder: "",
    editPrefill: "",
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Get the active device config, or null if nothing is out */
export function getActiveDeviceConfig(device: DeviceState): DeviceConfig | null {
  if (device.activeDevice === "phone") return device.hPhone;
  if (device.activeDevice === "pad") return device.hPad;
  return null;
}

/** Check if the device overlay should be interactive (fully raised) */
export function isDeviceReady(device: DeviceState): boolean {
  return device.animPhase === "held" && device.activeDevice !== null;
}

/** Get notification count for a specific app */
export function getAppBadge(device: DeviceState, appId: string): number {
  const n = device.notifications.find(n => n.appId === appId);
  return n?.count ?? 0;
}

/** Set notification count for an app (0 to clear) */
export function setAppBadge(device: DeviceState, appId: string, count: number): void {
  const existing = device.notifications.find(n => n.appId === appId);
  if (count <= 0) {
    device.notifications = device.notifications.filter(n => n.appId !== appId);
  } else if (existing) {
    existing.count = count;
  } else {
    device.notifications.push({ appId, count });
  }
}

/** Total unread badge count across all apps */
export function getTotalBadgeCount(device: DeviceState): number {
  return device.notifications.reduce((sum, n) => sum + n.count, 0);
}
